import {
    keepPreviousData,
    useMutation,
    useQuery,
    useSuspenseQuery,
    type UseQueryOptions,
    type UseSuspenseQueryOptions,
    type UseSuspenseQueryResult,
} from '@tanstack/react-query';
import type { AxiosRequestConfig } from 'axios';
import type {
    AddressContactDto,
    AddressDto,
    LocaleDto,
    PatientDtoGender,
    PatientDtoResidencyPermit,
} from '../../../api/generated/model';
import { request } from '../../axios';
import { ReferencePersonDto } from '../model/bezugspersonDto';

export enum PersonLivingStatus {
    ALIVE = 'ALIVE',
    DECEASED = 'DECEASED',
}

export interface IPersonListItem {
    id: string;
    firstName: string;
    lastName: string;
    birthDate: string;
    gender: PatientDtoGender | null;
    created: string | null;
    salutation?: string | null;
    phone?: string | null;
    mobilePhoneNumber?: string;
    privatePhoneNumber?: string;
    workPhoneNumber?: string;
    email: string | null;
}

export interface IPersonDetails extends IPersonListItem {
    salutation?: string;
    nationality: LocaleDto | null;
    origin: LocaleDto | null;
    arrival: string | null;
    asylumNumber: string | null;
    zemisNumber: string | null;
    additionalInformation: string | null;
    street: string | null;
    postalCode?: string | number,
    city?: string,
    state?: string,
    languages: LocaleDto[];
    residencyPermit: PatientDtoResidencyPermit | null;
    address: any;
    contact: AddressContactDto | null;
    bezugspersonen: ReferencePersonDto[];
    bezugspersonIds?: string[];
    livingStatus: PersonLivingStatus | null;
    note: string | null;
}

export interface UpdatePersonBody {
    id: string;
    salutation?: string;
    firstName: string;
    lastName: string;
    birthDate: string;
    gender: PatientDtoGender | null;
    mobilePhoneNumber?: string;
    privatePhoneNumber?: string;
    workPhoneNumber?: string;
    email?: string;
    nationality?: LocaleDto | null;
    origin?: LocaleDto | null;
    arrival?: string;
    asylumNumber?: string;
    zemisNumber?: string;
    languages?: LocaleDto[];
    additionalInformation?: string;
    street?: string;
    residencyPermit?: PatientDtoResidencyPermit | null;
    address: {
        address: AddressDto | null;
        contact: AddressContactDto | null;
        from: string;
        referenceType: 'PERSON_RESIDENCE';
    } | null;
    contact?: AddressContactDto | null;
    bezugspersonen?: ReferencePersonDto[];
    bezugspersonIds?: string[];
    postalCode?: number | string;
    city?: string;
    state?: string;
    livingStatus?: PersonLivingStatus | null;
    note?: string | null;
}

export interface GetPersonsResponse {
    content: IPersonListItem[];
    totalCount: number;
    page: number;
    size: number;
}

export interface GetPersonsParams {
    page?: number;
    size?: number;
    search?: string;
}

export function getPersons(params?: GetPersonsParams, options?: AxiosRequestConfig) {
    return request<GetPersonsResponse>({
        url: '/api/persons',
        method: 'GET',
        params: {
            page: params?.page ?? 0,
            size: params?.size ?? 20,
            search: params?.search,
        },
    }, options);
}

export function getPersonById(id: string) {
    return request<IPersonDetails>({
        url: `/api/persons/${id}`,
        method: 'GET',
    });
}

export function getGetPersonByIdQueryKey(id: string) {
    return ['persons', id] as const;
}

export function useGetPersonsQuery(
    params?: GetPersonsParams,
    enabled = true,
    options?: Partial<UseQueryOptions<GetPersonsResponse>>,
) {
    return useQuery({
        queryKey: [
            'persons',
            params?.page ?? 0,
            params?.size ?? 20,
            params?.search ?? '',
        ],
        queryFn: () => getPersons(params),
        enabled,
        placeholderData: keepPreviousData,
        staleTime: 5 * 60 * 1000,
        ...options,
    });
}

export async function searchPersonsForAutocomplete(
    search: string,
    params?: {
        page?: number;
        size?: number;
        signal?: AbortSignal;
    },
) {
    const trimmedSearch = search.trim();
    const page = params?.page ?? 0;
    const size = params?.size ?? 20;
    const requestOptions = params?.signal ? { signal: params.signal } : undefined;

    const response = await getPersons(
        { page, size, search: trimmedSearch || undefined },
        requestOptions,
    );

    return {
        items: response.content,
        hasMore: response.page + 1 < Math.ceil(response.totalCount / response.size),
    };
}

export function useGetPersonByIdSuspense(
    id: string,
    options?: {
        query?: Partial<UseSuspenseQueryOptions<IPersonDetails>>;
    },
): UseSuspenseQueryResult<IPersonDetails> & {
    queryKey: ReturnType<typeof getGetPersonByIdQueryKey>;
} {
    const queryKey = getGetPersonByIdQueryKey(id);

    const query = useSuspenseQuery({
        queryKey,
        queryFn: () => getPersonById(id),
        ...options?.query,
    }) as UseSuspenseQueryResult<IPersonDetails> & {
        queryKey: ReturnType<typeof getGetPersonByIdQueryKey>;
    };

    query.queryKey = queryKey;
    return query;
}

export function updatePerson(data: UpdatePersonBody) {
    return request<IPersonDetails>({
        url: '/api/persons',
        method: 'PUT',
        data,
    });
}

export function useUpdatePerson(options?: {
    mutation?: {
        onSuccess?: (data: IPersonDetails) => void;
        onError?: () => void;
    };
}) {
    return useMutation({
        mutationFn: async ({ data }: { data: UpdatePersonBody }) => updatePerson(data),
        ...options?.mutation,
    });
}

/**
 * Keep this only if you still need "fetch all persons" somewhere else.
 */
export async function getAllPersons() {
    const firstPage = await getPersons({ page: 0, size: 100 });

    const totalCount = firstPage.totalCount;
    const size = firstPage.size;
    const totalPages = Math.ceil(totalCount / size);

    if (totalPages <= 1) {
        return firstPage.content;
    }

    const remainingPagePromises = Array.from({ length: totalPages - 1 }, (_, index) =>
        getPersons({ page: index + 1, size }),
    );

    const remainingPages = await Promise.all(remainingPagePromises);

    return [firstPage, ...remainingPages].flatMap((page) => page.content);
}

export function useGetAllPersonsQuery(enabled = true) {
    return useQuery({
        queryKey: ['persons', 'all'],
        queryFn: getAllPersons,
        enabled,
        staleTime: 5 * 60 * 1000,
    });
}
